/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import type { ServiceWithEstimate } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Image,
  Skeleton,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import type { JSX } from 'react';
import BookingServiceInfoTooltip from './booking-service-info-tooltip';
import { surgeIcon } from './service-shared-utils';
import classes from './services.module.css';
import IconCapacity from '@/assets/icons/house-moving/hm-capacity-icon.svg';
import IconHmSupporter from '@/assets/icons/house-moving/hm-supporters-icon.svg';

type HmServiceListProps = {
  hmEstimatedServicesGroup: Record<string, ServiceWithEstimate[]>;
  activeItem: string;
  localeSuffix: string;
  isMobile: boolean;
  hasServiceAvailable: boolean;
  isEstimatingIntercityOrder: any[];
  isEstimatingNewOrder: any[];
  surgePriceConfig: any[] | undefined;
  onSelectVehicle: (value: string) => void;
  onSelectService: () => void;
};

export function HmServiceList({
  hmEstimatedServicesGroup,
  activeItem,
  localeSuffix,
  isMobile,
  hasServiceAvailable,
  isEstimatingIntercityOrder,
  isEstimatingNewOrder,
  surgePriceConfig,
  onSelectVehicle,
  onSelectService,
}: HmServiceListProps): JSX.Element | null {
  if (
    !hmEstimatedServicesGroup ||
    Object.keys(hmEstimatedServicesGroup).length === 0
  ) {
    return null;
  }

  const renderHmServiceItem = (
    service: ServiceWithEstimate,
    isRecommended = true
  ) => {
    const isServiceSelected = activeItem === service._id;
    const isDisabled = !isRecommended;
    return (
      <Accordion.Item
        component="div"
        style={(theme) => ({
          border: `1px solid ${
            isServiceSelected
              ? theme.colors.primary[4]
              : isRecommended
                ? theme.colors.neutral[3]
                : theme.colors.neutral[2]
          }`,
          borderRadius: 8,
        })}
        bg={
          isDisabled ? 'neutral.1' : isServiceSelected ? 'primary.0' : 'white'
        }
        key={service._id}
        value={service._id}
        pos="relative"
        mt={8}
      >
        <Accordion.Control
          style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}
        >
          <Box
            className={classes.button}
            p={0}
            w="100%"
            h="auto"
            bg="transparent"
            style={{
              position: 'relative',
              border: 'none',
              display: 'flex',
              alignItems: 'stretch',
              width: '100%',
              padding: 0,
            }}
          >
            <Stack gap={rem(2)} w="100%" justify="flex-start">
              <Group
                wrap="nowrap"
                justify="space-between"
                w="100%"
                gap={rem(4)}
              >
                <Group wrap="nowrap" w="100%" gap={rem(4)}>
                  <Title
                    ta="left"
                    lineClamp={1}
                    order={5}
                    fw={600}
                    c={isDisabled ? 'neutral.5' : 'neutral.7'}
                  >
                    {(service as any)?.estimate_package?.name}
                  </Title>
                  <span>
                    <BookingServiceInfoTooltip service={service} />
                  </span>
                  {service?.badge && (
                    <Badge
                      style={{ textTransform: 'capitalize' }}
                      bg={service.badge.background_color ?? 'red.6'}
                      c={service.badge.text_color ?? 'white'}
                      size="md"
                      radius="xl"
                    >
                      {service.badge[`description_${localeSuffix}`] || 'Mới'}
                    </Badge>
                  )}
                </Group>
                {isRecommended && (
                  <Box flex="none">
                    {(service as any).estimate_data?.data ? (
                      <Group
                        wrap="nowrap"
                        gap={rem(4)}
                        align="end"
                        justify="center"
                      >
                        {(service as any).estimate_data.data.subtotal_price >
                        (service as any).estimate_data.data.total_price ? (
                          <Text size="xs" c="neutral.5" td="line-through">
                            {formatCurrency(
                              (service as any).estimate_data.data.subtotal_price
                            )}
                          </Text>
                        ) : null}
                        <Title order={6} c="primary.5">
                          {formatCurrency(
                            (service as any).estimate_data.data.total_price
                          )}
                        </Title>
                        {surgeIcon(service, surgePriceConfig)}
                      </Group>
                    ) : isEstimatingIntercityOrder?.length > 0 ||
                      isEstimatingNewOrder.length > 0 ? (
                      <Skeleton width="100%" height={rem(16)} />
                    ) : null}
                  </Box>
                )}
              </Group>
              <Group gap={4}>
                <IconCapacity />
                <Text
                  c={isDisabled ? 'neutral.5' : 'neutral.6'}
                  size="sm"
                  fw={500}
                  align="left"
                >
                  {(service as any)?.estimate_package?.capacity_description}
                </Text>
              </Group>
              <Group gap={4}>
                <IconHmSupporter />
                <Text
                  c={isDisabled ? 'neutral.5' : 'neutral.6'}
                  size="sm"
                  fw={500}
                  align="left"
                >
                  {(service as any)?.estimate_package?.supporters_description}
                </Text>
              </Group>
            </Stack>
            {isServiceSelected && (
              <Box
                hiddenFrom="sm"
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                height="100%"
                width={rem(4)}
                bg="primary.6"
              />
            )}
          </Box>
        </Accordion.Control>
        {isRecommended && (
          <Accordion.Panel style={{ paddingBottom: 0 }}>
            <Image
              src={service?.vehicle_info?.images?.[0] ?? ''}
              alt="vehicle-image"
              fit="contain"
            />
          </Accordion.Panel>
        )}
      </Accordion.Item>
    );
  };

  const servicesContent = (
    <Stack gap={rem(24)} px={rem(16)} py={rem(12)}>
      {Object.entries(hmEstimatedServicesGroup).map(([groupName, services]) => (
        <Stack gap="sm" key={groupName}>
          <Title order={4}>{groupName}</Title>
          <Stack gap="xs">
            {services.map((s) => (
              <Accordion
                key={s._id}
                value={activeItem}
                onChange={onSelectVehicle}
                styles={{ chevron: { display: 'none' } }}
                variant="separated"
              >
                {renderHmServiceItem(s, true)}
              </Accordion>
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );

  if (isMobile) {
    return (
      <>
        {servicesContent}
        <Card
          pos="sticky"
          p={16}
          bottom={0}
          bg="white"
          w="100%"
          styles={{
            root: {
              boxShadow: '0px -2px 4px 0px rgba(0, 0, 0, 0.10)',
            },
          }}
        >
          <Button
            disabled={!hasServiceAvailable}
            m="auto"
            fullWidth
            size="md"
            mih={rem(48)}
            onClick={onSelectService}
          >
            Xác nhận
          </Button>
        </Card>
      </>
    );
  }

  return servicesContent;
}
